// lib/leasing/enrichNote.ts
// Shared helpers for documenting a brand-enrichment outcome as a concise markdown note
// in the left chat. Used by BOTH the Source right-bar panel (SourceBrandPanel) and the
// agent's ENRICH_BRAND command, so the conversation log reads identically whoever
// triggered the enrich (mirrors how Studio documents right-bar actions).

import { CNCC_SECTORS } from '#types/types';

export type EnrichBrandLike = {
  name?: string;
  websiteUrl?: string | null;
  declaredNetworkSize?: number | null;
  positioningNote?: string | null;
  logoUrl?: string | null;
  contactIds?: string[];
  sortableTotalRevenue?: number | null;
  revenueConfidence?: number | null;
  revenueIsEstimate?: boolean | null;
  legalEntityCount?: number | null;
  legalEntityIds?: string[];
  locations?: unknown[];
  locationCount?: number | null;
  surfaceStandard?: number | null;
  surfaceMin?: number | null;
  surfaceMax?: number | null;
  cnccSector?: string | null;
  cnccSubFamily?: string | null;
  cnccActivity?: string | null;
};

export const ENRICH_DIMS = ['website', 'network', 'positioning', 'category', 'surface', 'logo', 'contacts', 'financials', 'legal', 'stores'] as const;

// The 7 CNCC sectors (Conseil National des Centres Commerciaux) are the canonical
// CNCC_SECTORS referential in types/types.ts. cnccSector stores the letter.
export const formatCnccSector = (v: string | null | undefined): string | null => {
  if (!v) return null;
  const key = v.trim().toUpperCase();
  const label = (CNCC_SECTORS as Record<string, string>)[key];
  return label ? `${label} (${key})` : v;
};

// Typical store surface: "≈450 m²" from the standard figure, else a min-max range.
export const formatSurface = (
  b: { surfaceStandard?: number | null; surfaceMin?: number | null; surfaceMax?: number | null },
): string | null => {
  if (typeof b.surfaceStandard === 'number' && b.surfaceStandard > 0) return `≈${Math.round(b.surfaceStandard)} m²`;
  const min = typeof b.surfaceMin === 'number' && b.surfaceMin > 0 ? Math.round(b.surfaceMin) : null;
  const max = typeof b.surfaceMax === 'number' && b.surfaceMax > 0 ? Math.round(b.surfaceMax) : null;
  if (min && max) return `${min}-${max} m²`;
  if (min) return `≥${min} m²`;
  if (max) return `≤${max} m²`;
  return null;
};

// USD exists since SEC EDGAR filings joined the verified set. Currency is optional and defaults
// to EUR because every caller predating it is a French-registry figure; only the revenue chart
// passes it today. An unknown code falls back to itself as a prefix rather than lying with a
// symbol.
const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };
export const formatRevenue = (v: number | null | undefined, currency?: string | null): string | null => {
  if (typeof v !== 'number' || !isFinite(v) || v <= 0) return null;
  const code = (currency ?? 'EUR').toUpperCase();
  const sym = CURRENCY_SYMBOLS[code] ?? `${code} `;
  if (v >= 1e9) return `${sym}${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${sym}${(v / 1e6).toFixed(0)}M`;
  if (v >= 1e3) return `${sym}${(v / 1e3).toFixed(0)}K`;
  return `${sym}${Math.round(v)}`;
};

// The confidence band is GONE, deliberately, and this note is here so nobody rebuilds it.
//
// It rendered `revenueConfidence` as "High (100%)". That number was never a measure of data
// quality: it was the model's own answer to "how sure are you?", stored verbatim. The brand it was
// displayed on carried the turnover of an unrelated company, and the model was 100% sure of that
// too. A self-rating printed as a quality badge is worse than no badge, because it manufactures
// trust in the one case where trust is least warranted.
//
// What replaces it is not a better number. It is the entity, the period and the citation - things
// a reader can check. See lib/leasing/factGate.ts.


// Financials display for a VERIFIED figure: the amount, whose accounts it is, and which year.
// e.g. "€7.4M · France · 2024". No confidence badge, no "est." - an estimate never reaches this
// function, it has its own labelled block (formatEstimateValue).
export const formatFinancialValue = (
  b: {
    sortableTotalRevenue?: number | null;
    revenueIsEstimate?: boolean | null;
    revenueEntityName?: string | null;
    revenueEntityCountry?: string | null;
    revenuePeriod?: string | null;
  },
  lang: string,
): string | null => {
  const rev = formatRevenue(b.sortableTotalRevenue);
  // Only a VERIFIED figure ever reaches here: the mapper nulls the column otherwise
  // (lib/leasing/factGate.ts), so "no revenue" and "no proof" collapse to the same honest blank.
  if (!rev) return null;
  // WHOSE REVENUE THIS IS. Our financials come from a national registry, so the figure belongs to
  // one legal entity in one country: "EUR 170.46M" on Pull & Bear is PULL & BEAR FRANCE, not the
  // worldwide brand and not Inditex. Read as global it is a serious commercial error, and until
  // now nothing on any surface said which it was. The entity name is the honest label - it is
  // what the accounts are filed under - and the country carries it when the name does not already.
  const scope = formatRevenueScope(b, lang);
  // The PERIOD, because a true number attributed to the wrong year is still a wrong number, and
  // "€7.4M" with no year silently reads as "now".
  const period = typeof b.revenuePeriod === 'string' && b.revenuePeriod.trim() ? b.revenuePeriod.trim() : null;
  const bits = [scope, period].filter(Boolean).join(' · ');
  return `${rev}${bits ? ` · ${bits}` : ''}`;
};

/**
 * The MODELLED revenue, and it says so in words.
 *
 * Deliberately shares no formatting with formatFinancialValue: no "≈" glyph doing the work of a
 * caption, no confidence band, no chance of the two being read as the same kind of thing at a
 * glance. It carries its own arithmetic, because a number a reader can reconstruct is a number
 * they can disbelieve - which is the entire difference between an estimate and a claim.
 */
export const formatEstimateValue = (
  est: { revenue: number | null; year?: number | null; inputs?: Record<string, unknown> | null } | null | undefined,
  lang: string,
): string | null => {
  const rev = formatRevenue(est?.revenue);
  if (!rev) return null;
  const fr = lang === 'fr';
  const i = est?.inputs ?? {};
  const num = (k: string): number | null => (typeof i[k] === 'number' ? (i[k] as number) : null);
  const stores = num('storeCount');
  const sqm = num('surfaceSqm');
  const perSqm = num('salesPerSqm');
  const employees = num('employeesMidpoint');
  const perEmployee = num('revenuePerEmployee');
  // Two models, two arithmetics, and the line always shows the one that produced the number:
  // a figure a reader can reconstruct is a figure they can disbelieve.
  const workings =
    employees && perEmployee
      ? ` (~${employees} ${fr ? 'salariés' : 'employees'} × ${formatRevenue(perEmployee) ?? perEmployee}/${fr ? 'salarié' : 'employee'})`
      : stores && sqm && perSqm
        ? ` (${stores} ${fr ? 'pdv' : 'stores'} × ${sqm} m² × ${formatRevenue(perSqm) ?? perSqm}/m²)`
        : '';
  return `${fr ? 'Estimation' : 'Estimate'} ${rev}${workings}, ${fr ? 'modélisé, non vérifié' : 'modelled, not verified'}`;
};

/**
 * The entity a revenue figure belongs to, short enough to sit on one line.
 *
 * Prefers the registered legal name, because that is literally who filed the accounts. Falls back
 * to the country when there is no name. Returns null rather than guessing: a figure whose entity we
 * do not know must not be labelled as if we did.
 */
export const formatRevenueScope = (
  b: { revenueEntityName?: string | null; revenueEntityCountry?: string | null },
  lang: string,
): string | null => {
  const country = typeof b.revenueEntityCountry === 'string' ? b.revenueEntityCountry.trim().toUpperCase() : '';
  if (!country) return null;
  // LEAD WITH THE GEOGRAPHY, not the entity name. The registered names we hold are short ("Nespresso",
  // "Alain Afflelou") rather than the full "NESPRESSO FRANCE SAS", so a name-led label conveys no
  // scope at all - and scope is the entire point. "France only" cannot be misread as worldwide.
  // The entity itself stays one click away on the Legal tab, which is where the evidence belongs.
  const COUNTRY_NAMES: Record<string, { en: string; fr: string }> = {
    FR: { en: 'France', fr: 'France' },
    ES: { en: 'Spain', fr: 'Espagne' },
    IT: { en: 'Italy', fr: 'Italie' },
    DE: { en: 'Germany', fr: 'Allemagne' },
    BE: { en: 'Belgium', fr: 'Belgique' },
    GB: { en: 'UK', fr: 'Royaume-Uni' },
  };
  const label = COUNTRY_NAMES[country]?.[lang === 'fr' ? 'fr' : 'en'] ?? country;
  return lang === 'fr' ? `${label} uniquement` : `${label} only`;
};

export const hostnameOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
  }
};

const DIM_LABELS: Record<string, { en: string; fr: string }> = {
  website: { en: 'Website', fr: 'Site web' },
  network: { en: 'Network size', fr: 'Taille de réseau' },
  positioning: { en: 'Positioning', fr: 'Positionnement' },
  category: { en: 'Category (CNCC)', fr: 'Catégorie (CNCC)' },
  surface: { en: 'Surface', fr: 'Surface' },
  logo: { en: 'Logo', fr: 'Logo' },
  contacts: { en: 'Contacts', fr: 'Contacts' },
  financials: { en: 'Financials', fr: 'Finances' },
  legal: { en: 'Legal entities', fr: 'Entités légales' },
  stores: { en: 'Stores', fr: 'Magasins' },
};

// Current human-readable value of one aspect (null when the brand has no data for it).
const dimValue = (b: EnrichBrandLike | undefined, dim: string, lang: string): string | null => {
  if (!b) return null;
  switch (dim) {
    case 'website': return b.websiteUrl ? hostnameOf(b.websiteUrl) : null;
    case 'network': return typeof b.declaredNetworkSize === 'number' ? `${b.declaredNetworkSize} ${lang === 'fr' ? 'pdv' : 'stores'}` : null;
    case 'positioning': return b.positioningNote || null;
    case 'category': return b.cnccActivity ? `${b.cnccActivity} (${(b.cnccSector ?? '').toUpperCase()})` : formatCnccSector(b.cnccSector);
    case 'surface': return formatSurface(b);
    case 'logo': return b.logoUrl ? (lang === 'fr' ? 'présent' : 'present') : null;
    case 'contacts': { const n = b.contactIds?.length ?? 0; return n > 0 ? `${n} ${lang === 'fr' ? 'trouvés' : 'found'}` : null; }
    case 'financials': return formatFinancialValue(b, lang);
    case 'legal': { const n = (b.legalEntityCount ?? b.legalEntityIds?.length) ?? 0; return n > 0 ? `${n} ${n > 1 ? (lang === 'fr' ? 'entités' : 'entities') : (lang === 'fr' ? 'entité' : 'entity')}` : null; }
    // Checked, not fetched: the count is how many claimed stores exist to verify. Reads
    // locationCount first, the enrich route's DTO carries the count, not the array, so keying
    // off `locations` alone reported "no data found: Stores" on a brand with 21 of them.
    case 'stores': { const n = b.locationCount ?? b.locations?.length ?? 0; return n > 0 ? `${n} ${lang === 'fr' ? 'magasins' : 'stores'}` : null; }
    default: return null;
  }
};

export interface StoresVerification {
  checked: number;
  deleted: number;
  notFound: number;
  skipped: number;
}

/**
 * What a store verification actually did, in one line.
 *
 * The stores dimension is the only one that CHECKS instead of filling, so the before/after
 * field diff every other dimension uses cannot describe it: the store count is usually
 * identical afterwards, which the diff reads as "nothing happened" and reports as "already up
 * to date". That is what /verify-stores told the user while it had just checked ten stores.
 * The route already returns the real numbers; this is the only place they are worded, so the
 * chat note and the button toast cannot drift apart.
 */
export const describeStoresVerification = (
  stores: StoresVerification | undefined,
  storesTotal: number | undefined,
  lang: string,
): { ok: boolean; message: string } => {
  const fr = lang === 'fr';
  if (!stores || stores.checked === 0) {
    return { ok: false, message: fr ? 'aucun magasin à vérifier' : 'no stores to check' };
  }
  const tail = stores.skipped > 0
    ? (fr ? `, ${stores.skipped} non vérifiés` : `, ${stores.skipped} not checked`)
    : '';
  return {
    ok: true,
    message: fr
      ? `${stores.checked} magasins vérifiés, ${stores.notFound} introuvables, ${stores.deleted} supprimés${tail}. Total : ${storesTotal ?? '?'}.`
      : `${stores.checked} stores verified, ${stores.notFound} not found, ${stores.deleted} deleted${tail}. Total: ${storesTotal ?? '?'}.`,
  };
};

/**
 * One-line outcome for a single-aspect enrich, for the toast next to the button.
 *
 * WHY: the per-field path used to show nothing at all on success, so "found nothing" and
 * "worked" looked identical, a brand with no website silently kept saying Logo: Missing.
 * The chat note covers this, but it is dropped when there is no active chat session, and
 * the user is looking at the right panel anyway.
 */
export const describeEnrichOutcome = (
  before: EnrichBrandLike | undefined,
  after: EnrichBrandLike | undefined,
  dim: string,
  lang: string,
): { ok: boolean; message: string } => {
  const label = DIM_LABELS[dim]?.[lang === 'fr' ? 'fr' : 'en'] ?? dim;
  const b = dimValue(before, dim, lang);
  const a = dimValue(after ?? before, dim, lang);
  if (a && !b) return { ok: true, message: `${label}: ${a}` };
  // "Already up to date" was read as a refusal when the user had explicitly asked for THIS field ,
  // often precisely because they doubt the stored value. Say what is held instead, so the answer
  // is the data rather than a status.
  if (a) return { ok: true, message: `${label}: ${a}` };
  return { ok: false, message: `${lang === 'fr' ? 'aucune donnée trouvée' : 'no data found'}: ${label}` };
};

// Build a concise markdown note documenting what an enrich action changed. Honest:
// lists what got filled and what came back empty, so the log reflects reality.
export const buildEnrichNote = (
  brandName: string,
  before: EnrichBrandLike | undefined,
  after: EnrichBrandLike | undefined,
  dims: readonly string[],
  lang: string,
  stores?: { stores?: StoresVerification; storesTotal?: number },
): string => {
  const label = (dim: string) => DIM_LABELS[dim]?.[lang === 'fr' ? 'fr' : 'en'] ?? dim;
  const filled: string[] = [];
  const empty: string[] = [];
  // A checked dimension reports what it checked. Kept out of filled/empty on purpose: an
  // unchanged store count is the expected outcome of a verification, not a silent one.
  const storeLine = dims.includes('stores') && stores
    ? describeStoresVerification(stores.stores, stores.storesTotal, lang).message
    : null;
  for (const dim of dims) {
    if (dim === 'stores' && storeLine) continue;
    const b = dimValue(before, dim, lang);
    const a = dimValue(after ?? before, dim, lang);
    if (a && !b) filled.push(`${label(dim)}: ${a}`);
    else if (!a) empty.push(label(dim));
  }
  const lines = [`**${brandName}**, ${lang === 'fr' ? 'données enrichies' : 'data enriched'}`];
  if (storeLine) lines.push(`- ${storeLine}`);
  for (const f of filled) lines.push(`- ${f}`);
  if (empty.length) lines.push(`- ${lang === 'fr' ? 'aucune donnée trouvée' : 'no data found'}: ${empty.join(', ')}`);
  if (!filled.length && !empty.length && !storeLine) lines.push(`- ${lang === 'fr' ? 'déjà à jour' : 'already up to date'}`);
  return lines.join('\n');
};

// Append a documentation message to the active left-chat session (Studio pattern:
// kernel.updateActiveSessionMessages with an assistant-role note). `k` is the kernel handle.
